/**
 * ConsentExecutionCheckbox — la case de rétractation, article VI.53 CDE.
 *
 * Elle se pose JUSTE AVANT le bouton de paiement, sur tous les parcours
 * d'achat, et le bouton reste désactivé tant qu'elle n'est pas cochée.
 *
 * Trois règles, non négociables :
 *
 *  1. JAMAIS pré-cochée. Un consentement pré-coché n'en est pas un
 *     (art. VI.53 CDE, et art. 4.11 RGPD pour le principe). L'état
 *     initial appartient à l'appelant, qui l'initialise à `false`.
 *  2. Le TEXTE ne se reformule pas. Il est reproduit ici mot pour mot,
 *     et recopié à l'identique côté serveur
 *     (backend/services/consent_execution.py) pour que la preuve stockée
 *     dise exactement ce que le client a lu.
 *  3. « Conditions générales » est un lien vers /conditions-generales,
 *     en NOUVEL ONGLET : cliquer pour lire les CGV ne doit pas faire
 *     perdre au client son panier ni sa place dans le parcours.
 *
 * La case est le seul endroit du produit où un texte juridique est
 * affiché en dur dans un composant. C'est voulu : le déplacer dans un
 * catalogue i18n en ferait une chaîne comme les autres, traduisible et
 * reformulable par mégarde. FR uniquement pour l'instant, comme les
 * quatre documents.
 */
import React from 'react';

export default function ConsentExecutionCheckbox({
  checked,
  onChange,
  disabled = false,
  id = 'consent-execution',
  testId = 'consent-execution',
  // `dark` : contraste inversé, pour une case posée sur fond sombre. Un
  // texte gris sombre y serait illisible, et un texte illisible n'est pas
  // un consentement éclairé. Aucun appelant ne l'utilise depuis que la
  // case a quitté la carte Pro de /plans (fond bleu) pour l'étape de
  // confirmation (fond blanc) ; il reste pour le prochain fond sombre.
  dark = false,
  style,
}) {
  const textColor = dark ? 'rgba(255,255,255,.88)' : '#4b5563';
  const linkColor = dark ? '#ffffff' : '#1a56db';
  return (
    <label
      htmlFor={id}
      data-testid={`${testId}-label`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        // 44px de cible tactile pour la case elle-même (cf. conventions
        // responsive 0e71604) : sur 390 px, une case de 14 px se rate.
        padding: '10px 0',
        cursor: disabled ? 'default' : 'pointer',
        textAlign: 'left',
        ...style,
      }}
    >
      <input
        id={id}
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        style={{
          flex: 'none',
          width: 18,
          height: 18,
          marginTop: 1,
          accentColor: '#1a56db',
          cursor: disabled ? 'default' : 'pointer',
        }}
      />
      <span
        style={{
          fontSize: 12.5,
          lineHeight: 1.55,
          color: textColor,
          minWidth: 0,
        }}
      >
        {/* Texte EXACT, au caractère près, identique à celui recopié dans
            backend/services/consent_execution.py (apostrophes droites
            comprises) : la preuve stockée doit dire mot pour mot ce que le
            client a lu. Ne pas « embellir » la typographie ici sans faire
            la même chose là-bas. */}
        Je demande l'exécution immédiate du service et je reconnais perdre mon droit de
        rétractation une fois le service exécuté, conformément à l'article VI.53 du Code de
        droit économique. J'accepte les{' '}
        <a
          href="/conditions-generales"
          target="_blank"
          rel="noopener noreferrer"
          // Le clic sur le lien ne doit pas cocher la case : le <label>
          // englobe tout, donc sans ça, ouvrir les CGV consentirait.
          onClick={(e) => e.stopPropagation()}
          style={{ color: linkColor, textDecoration: 'underline', textUnderlineOffset: 2 }}
          data-testid={`${testId}-cgv-link`}
        >
          Conditions générales
        </a>
        .
      </span>
    </label>
  );
}
